import { IAppState, IMembers } from '../../types/state.d';
import { Page, PageInfo, PageType } from '../../../types/graphql';
import {
  getMembers,
  getPageInfo,
  getTeamGuid,
} from '../../selectors/apiSelectors';

import { ERROR_MESSAGES } from '../../consts';
import apolloClient from '../../apolloClient';
import { gql } from '@apollo/client';

export type MemberListItem = {
  numberOfMembers: number;
  members: IMembers;
  pageInfo: PageInfo;
};

export type MemberItem = {
  userGuid: string;
  firstName: string | null;
  lastName: string | null;
  profileImage: string | null;
  fundraisingPageGuid: string;
  fundraisingPageShortName: string;
  fundraisingPageName: string;
  pageUrl: string;
  pageType: PageType;
};

const toMemberItem = (node: Page): MemberItem => {
  const firstName = node.owner!.name!.split(' ').slice(0, -1).join(' ');
  const lastName = node.owner!.name!.split(' ').slice(-1)[0];

  return {
    userGuid: node.owner!.legacyId ?? '',
    firstName,
    lastName,
    profileImage: node.owner!.avatar ?? '',
    fundraisingPageGuid: node.legacyId,
    fundraisingPageShortName: node.slug ?? '',
    fundraisingPageName: node.title,
    pageUrl: node.url,
    pageType: node.type,
  };
};

const fetchLegacyMembersList = async (
  getState: IAppState,
): Promise<MemberListItem> => {
  const pageType = PageType.Team;
  const teamGuid = await getTeamGuid(getState);
  const pageInfoData = await getPageInfo(getState);
  const members = await getMembers(getState);

  const { data, error } = await apolloClient.query({
    query: gql`
      query GetMembers($pageType: PageType!, $teamGuid: ID!, $after: String) {
        page(type: $pageType, id: $teamGuid) {
          legacyId
          relationships {
            members(first: 10, after: $after) {
              totalCount
              pageInfo {
                hasNextPage
                endCursor
              }
              nodes {
                id
                legacyId
                slug
                title
                url
                type
                owner {
                  id
                  legacyId
                  name
                  avatar
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      pageType: pageType,
      teamGuid: teamGuid,
      after: pageInfoData?.endCursor,
    },
  });

  const numberOfMembers = data.page.relationships.members.totalCount;
  const pageInfo = data.page.relationships.members.pageInfo;

  if (error) {
    throw new Error(ERROR_MESSAGES.GET_MEMBERS_LIST);
  }

  const mappedMembers = [
    ...members,
    ...data.page.relationships.members.nodes.map(toMemberItem),
  ];

  return {
    numberOfMembers,
    members: mappedMembers,
    pageInfo,
  };
};

export default fetchLegacyMembersList;
